import React, { useMemo } from 'react';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DesktopDateTimePicker } from '@mui/x-date-pickers/DesktopDateTimePicker';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import dayjs from 'dayjs';
import styled from 'styled-components';

const DateSelector = ({
  value,
  onChange,
  dateOnly = false,
  disabled = false,
  readOnlyInput = false,
  minutesStep,
  minDate,
  maxDate,
  matchDropdownStyle = false,
  className,
}) => {
  const formattedValue = useMemo(() => {
    if (!value) {
      return null;
    }

    if (dateOnly) {
      return dayjs(value).startOf('day');
    }

    return dayjs(value);
  }, [value, dateOnly]);

  const handleDateAccept = (newValue) => {
    if (!newValue) return;
    const updatedValue = dateOnly
      ? dayjs(newValue).startOf('day')
      : dayjs(newValue);
    onChange(updatedValue);
  };

  const now = dayjs();
  const hasMinDate = minDate !== null;
  const resolvedMinDate = hasMinDate ? (minDate || now) : undefined;

  const PickerComponent = dateOnly ? DesktopDatePicker : DesktopDateTimePicker;
  const pickerProps = dateOnly
    ? {
      minDate: resolvedMinDate,
      maxDate,
    }
    : {
      ampm: false,
      minDateTime: resolvedMinDate,
      maxDateTime: maxDate,
      ...(typeof minutesStep === 'number' ? { minutesStep } : {}),
    };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <TimeDateContainer className={className}>
        <DateContainer $matchDropdownStyle={matchDropdownStyle}>
          <PickerComponent
            value={formattedValue}
            onAccept={handleDateAccept}
            onChange={() => {}}
            format={dateOnly ? 'YYYY-MM-DD' : 'YYYY-MM-DD HH:mm'}
            disabled={disabled}
            {...pickerProps}
            slotProps={{
              textField: {
                variant: 'outlined',
                fullWidth: true,
                inputProps: {
                  readOnly: readOnlyInput,
                },
              },
            }}
          />
        </DateContainer>
      </TimeDateContainer>
    </LocalizationProvider>
  );
};

export default React.memo(DateSelector);

const TimeDateContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 24px;
  position: relative;
  align-self: stretch;
`;

const DateContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0px;
  position: relative;
  align-self: stretch;
  background-color: var(--white);
  border-radius: ${({ $matchDropdownStyle }) => ($matchDropdownStyle ? '8px' : '12px')};
  overflow: hidden;
  border: 1px solid;
  height: ${({ $matchDropdownStyle }) => ($matchDropdownStyle ? '40px' : '100%')};
  min-height: ${({ $matchDropdownStyle }) => ($matchDropdownStyle ? '40px' : 'unset')};
  width: ${({ $matchDropdownStyle }) => ($matchDropdownStyle ? '100%' : '210px')};
  border-color: ${({ $matchDropdownStyle }) => ($matchDropdownStyle ? '#c9c6cc' : 'var(--text)')};

  .MuiInputBase-root.MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline {
    border-color: var(--text);
  }

  .MuiInputBase-root.MuiOutlinedInput-root .MuiInputBase-input {
    font-size: 14px;
  }

  ${({ $matchDropdownStyle }) => $matchDropdownStyle && `
    .MuiInputBase-root.MuiOutlinedInput-root {
      height: 40px;
      min-height: 40px;
      width: 100%;
      color: #7b7382;
    }

    .MuiInputBase-root.MuiOutlinedInput-root .MuiInputBase-input {
      height: 40px;
      box-sizing: border-box;
      padding: 0 8px;
      line-height: 40px;
      color: #7b7382;
      font-size: 14px;
      font-weight: 400;
    }

    .MuiInputAdornment-root .MuiIconButton-root,
    .MuiInputAdornment-root .MuiSvgIcon-root {
      color: #7b7382;
    }
  `}

  .MuiOutlinedInput-notchedOutline {
    border-radius: ${({ $matchDropdownStyle }) => ($matchDropdownStyle ? '8px' : '12px')};
    border: 0px solid var(--text);
  }

  fieldset {
    display: none;
  }
`;
